/**
 * Single source of truth for the political identity of this site.
 *
 * Every value here is reproduced in legally mandated places — the campaign
 * labelling in the footer (§ 30 zákona č. 491/2001 Sb.) and the controller
 * identity in the Art. 13 GDPR notice on /soukromi. Nothing in this object may
 * be duplicated as a literal anywhere else in the codebase; import from here.
 *
 * There are no defaults. An empty or whitespace-only field is a build failure:
 * `NonEmpty<T>` rejects the empty string literal at compile time, and
 * `assertIdentityComplete()` (run by the build, by the API at boot, and by the
 * test suite) rejects whitespace at runtime.
 */

export interface Identity {
  readonly movement: string;
  readonly list: string;
  readonly ico: string;
  readonly address: string;
  readonly dataBox: string;
  readonly contact: string;
  readonly phone: string;
  /**
   * Rendered as an additional labelling line in the footer when non-empty.
   * Starts empty by design — a coalition partner can be added here later
   * without touching a single component.
   */
  readonly coalitionPartners: readonly string[];
  readonly zadavatel: string;
  readonly zpracovatel: string;
}

export const IDENTITY = {
  movement: 'Hnutí JSME TEAM',
  list: 'JSME PRAHA',
  ico: '07157029',
  address: 'Pod strání 2220/22, 100 00 Praha',
  dataBox: 'rkmhw8v',
  contact: 'info@jsme.team',
  phone: '+420 608 303 542',
  coalitionPartners: [],
  zadavatel: 'Hnutí JSME TEAM',
  zpracovatel: 'Hnutí JSME TEAM',
} as const satisfies Identity;

/** Election the candidate list is standing in. Used in copy, not in labelling. */
export const ELECTION = {
  name: 'Volby do Zastupitelstva hlavního města Prahy',
  dates: '9.–10. října 2026',
  listLeader: 'Jana Komrsková',
  movementChair: 'Lukáš Wagenknecht',
  mainSite: 'https://jsme.team',
} as const;

/** Fields that must carry a real value for the build to be shippable. */
const REQUIRED_FIELDS = [
  'movement',
  'list',
  'ico',
  'address',
  'dataBox',
  'contact',
  'phone',
  'zadavatel',
  'zpracovatel',
] as const satisfies readonly (keyof Identity)[];

/**
 * Throws if any mandatory identity field is missing or blank.
 * Called at module load below, so importing this file anywhere — client bundle,
 * API boot, or test — fails loudly rather than rendering an empty legal notice.
 */
export function assertIdentityComplete(identity: Identity = IDENTITY): void {
  const blank = REQUIRED_FIELDS.filter((field) => {
    const value = identity[field];
    return typeof value !== 'string' || value.trim().length === 0;
  });
  if (blank.length > 0) {
    throw new Error(
      `content/identity.ts: mandatory identity field(s) empty: ${blank.join(', ')}. ` +
        'These appear in campaign labelling and the GDPR notice and cannot be blank.',
    );
  }
}

assertIdentityComplete();

/**
 * Compile-time proof that no field was emptied.
 *
 * The mapped type turns any property whose type is exactly `''` into `never`,
 * which makes the assignment below fail to compile. Non-string fields
 * (coalitionPartners) pass through unchanged.
 */
type NoEmptyStrings<T> = { [K in keyof T]: T[K] extends '' ? never : T[K] };
const _identityHasNoEmptyLiterals: NoEmptyStrings<typeof IDENTITY> = IDENTITY;
void _identityHasNoEmptyLiterals;
